import { mdiMagnify } from "@mdi/js";
import Icon from "@mdi/react";
import { ChangeEvent, useEffect, useState } from "react";

import Input from "components/Input";
import FactoringCompany from "features/main/HaulPay/Shared/FactoringCompany";
import useDebounce from "hooks/useDebounce";
import { IFactoringCompany } from "models/HaulPay";
import { getFactoringCompanyAPI } from "services/api/haulpay";
import styles from "./style.module.scss";

const MINIMUM_SEARCH_LENGTH = 2;

interface IProps {
  factoring_id: string;
  company?: IFactoringCompany;
  onCompanySelected?: (company: IFactoringCompany) => void;
}

export default function FactoringCompanySelect({
  factoring_id,
  company,
  onCompanySelected
}: Readonly<IProps>) {
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearchError, setHasSearchError] = useState(false);
  const [factoringCompanies, setFactoringCompanies] = useState<IFactoringCompany[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [hasSelectedCompany, setHasSelectedCompany] = useState(Boolean(company));
  const debouncedSearch = useDebounce(searchValue, 500);
  const normalizedSearch = searchValue.trim();
  const canSearch = !hasSelectedCompany && normalizedSearch.length >= MINIMUM_SEARCH_LENGTH;
  const isWaitingForSearch = canSearch && normalizedSearch !== debouncedSearch.trim();

  const handleValueChange = (event: ChangeEvent) => {
    setHasSelectedCompany(false);
    setSearchValue((event.target as HTMLInputElement).value);
  };

  const handleClickCompany = (company: IFactoringCompany) => {
    setHasSelectedCompany(true);
    setSearchValue(company.name);
    setFactoringCompanies([]);
    if (onCompanySelected) onCompanySelected(company);
  };

  useEffect(() => {
    if (company) {
      setSearchValue(company.name);
      setHasSelectedCompany(true);
    }
  }, [company]);

  useEffect(() => {
    let ignoreResult = false;

    const fetchCompany = async () => {
      const searchQuery = debouncedSearch.trim();
      if (!factoring_id || hasSelectedCompany || searchQuery.length < MINIMUM_SEARCH_LENGTH) {
        setFactoringCompanies([]);
        setHasSearchError(false);
        setIsLoading(false);
        return;
      }

      setFactoringCompanies([]);
      setHasSearchError(false);
      setIsLoading(true);

      try {
        const { data: factoringCompanyResult } = await getFactoringCompanyAPI(searchQuery);
        if (!ignoreResult) setFactoringCompanies(factoringCompanyResult.results ?? []);
      } catch {
        if (!ignoreResult) setHasSearchError(true);
      } finally {
        if (!ignoreResult) setIsLoading(false);
      }
    };

    fetchCompany();
    return () => {
      ignoreResult = true;
    };
  }, [debouncedSearch, factoring_id, hasSelectedCompany]);

  let searchResults = (
    <p className={styles.noListText}>No factoring companies match your search.</p>
  );
  if (hasSearchError) {
    searchResults = (
      <p className={styles.noListText}>We couldn’t load factoring companies. Try again.</p>
    );
  } else if (factoringCompanies.length) {
    searchResults = (
      <>
        {factoringCompanies.map((factoringCompany) => (
          <FactoringCompany
            key={factoringCompany.id}
            factoring_company={factoringCompany}
            onClickCompany={() => handleClickCompany(factoringCompany)}
          />
        ))}
      </>
    );
  }

  return (
    <div className={styles.searchShipperDebtorContainer}>
      <Input
        aria-label="Search factoring companies"
        placeholder="Search by company name"
        value={searchValue}
        isLoading={isLoading || isWaitingForSearch}
        onChange={handleValueChange}
        inputClass="desktop:!h-14 desktop:!text-base"
        prefixNode={<Icon path={mdiMagnify} className={styles.searchIcon} />}
      />

      {!hasSelectedCompany && !canSearch ? (
        <p className={styles.searchHint}>Type at least two characters to search.</p>
      ) : null}

      {canSearch && !isLoading && !isWaitingForSearch ? (
        <div className={styles.resultsPanel} aria-live="polite">
          {searchResults}
        </div>
      ) : null}
    </div>
  );
}
